import posthog from 'posthog-js';
import { FeatureFlag } from './feature-flags';

export interface PostHogUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  rating?: number;
  gender?: string;
  federationId?: string;
  locationId?: string;
  clubId?: string;
}

// Re-export feature flags so existing imports from '@/lib/posthog' keep working
export { FeatureFlag } from './feature-flags';

export const posthogUtils = {
  /**
   * Identify a user in PostHog with their properties
   */
  identifyUser(user: PostHogUser) {
    if (typeof window === 'undefined') return; // Only run on client side

    try {
      // Identify the user with their unique ID
      posthog.identify(user.id, {
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        username: user.username,
        current_rating: user.rating,
        gender: user.gender,
        federation_id: user.federationId,
        location_id: user.locationId,
        club_id: user.clubId,
        // Add any other user properties you want to track
      });

      // Set user properties for future events
      posthog.setPersonProperties({
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        username: user.username,
        current_rating: user.rating,
        gender: user.gender,
        federation_id: user.federationId,
        location_id: user.locationId,
        club_id: user.clubId,
      });

      console.log('PostHog: User identified successfully', user.id);
    } catch (error) {
      console.error('PostHog: Failed to identify user', error);
    }
  },

  /**
   * Identify a user with minimal data (useful for registration)
   */
  identifyUserMinimal(userId: string, email: string, firstName?: string, lastName?: string) {
    if (typeof window === 'undefined') return;

    try {
      posthog.identify(userId, {
        email,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
      });

      posthog.setPersonProperties({
        email,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
      });

      console.log('PostHog: User identified with minimal data', userId);
    } catch (error) {
      console.error('PostHog: Failed to identify user with minimal data', error);
    }
  },

  /**
   * Reset user identification (for logout)
   */
  resetUser() {
    if (typeof window === 'undefined') return;

    try {
      posthog.reset();
      console.log('PostHog: User reset successfully');
    } catch (error) {
      console.error('PostHog: Failed to reset user', error);
    }
  },

  /**
   * Set a specific user property
   */
  setUserProperty(key: string, value: string | number | boolean | null) {
    if (typeof window === 'undefined') return;

    try {
      posthog.setPersonProperties({ [key]: value });
    } catch (error) {
      console.error('PostHog: Failed to set user property', error);
    }
  },

  /**
   * Get the current user ID from PostHog
   */
  getCurrentUserId(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      return posthog.get_distinct_id();
    } catch (error) {
      console.error('PostHog: Failed to get current user ID', error);
      return null;
    }
  },

  /**
   * Check if a feature flag is enabled for the current user.
   * Returns false on the server, or if PostHog is not available.
   * In development, returns true for all flags so they can be tested locally.
   */
  isFeatureEnabled(flag: FeatureFlag): boolean {
    if (typeof window === 'undefined') return false;
    if (process.env.NODE_ENV === 'development') return true;

    try {
      // posthog.isFeatureEnabled returns a boolean for simple on/off flags
      console.log('PostHog: Checking feature flag', flag, posthog.isFeatureEnabled(flag));
      return !!posthog.isFeatureEnabled(flag);
    } catch (error) {
      console.error('PostHog: Failed to evaluate feature flag', flag, error);
      return false;
    }
  },
};
